import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, Calendar, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const categories = [
  "All",
  "Career Transitions",
  "Emotional Readiness",
  "Platform Updates",
  "Research & Insights"
];

// Sample blog posts for initial display
const samplePosts = [
  {
    id: "1",
    title: "What Reflect. Discover. Become. Actually Means",
    slug: "reflect-discover-become-meaning",
    excerpt: "Understanding the three phases of transformation and how they work together to create lasting change.",
    category: "Platform Updates",
    image_url: "https://images.unsplash.com/photo-1763293203882-ce3ba78b54d7?w=600",
    created_at: new Date().toISOString(),
    is_featured: true
  },
  {
    id: "2",
    title: "America's AI Boom Is Real. Most Americans Will Never Feel It.",
    slug: "americas-ai-boom-inequality",
    excerpt: "The promise of AI transformation is everywhere, but who actually gets to benefit from it?",
    category: "Research & Insights",
    image_url: "https://images.unsplash.com/photo-1764549906172-0153db0825bc?w=600",
    created_at: new Date(Date.now() - 86400000).toISOString(),
    is_featured: false
  },
  {
    id: "3",
    title: "The Real Cost of Changing Careers",
    slug: "real-cost-career-change",
    excerpt: "Beyond the financial math, what does it really take to make a major professional transition?",
    category: "Career Transitions",
    image_url: "https://images.pexels.com/photos/5439140/pexels-photo-5439140.jpeg?w=600",
    created_at: new Date(Date.now() - 172800000).toISOString(),
    is_featured: false
  },
  {
    id: "4",
    title: "Why You Shouldn't Make a Career Decision When You're Burned Out",
    slug: "career-decisions-burnout",
    excerpt: "Burnout distorts how we see ourselves and our options. Here's why waiting might be the wisest move.",
    category: "Emotional Readiness",
    image_url: "https://images.unsplash.com/photo-1752650735615-9829d8008a01?w=600",
    created_at: new Date(Date.now() - 259200000).toISOString(),
    is_featured: false
  },
  {
    id: "5",
    title: "Mental Health Apps Promised to Help. They Were Designed to Keep You Engaged.",
    slug: "mental-health-apps-engagement",
    excerpt: "The dark patterns behind wellness apps and what ethical support should actually look like.",
    category: "Research & Insights",
    image_url: "https://images.pexels.com/photos/5712677/pexels-photo-5712677.jpeg?w=600",
    created_at: new Date(Date.now() - 345600000).toISOString(),
    is_featured: false
  },
  {
    id: "6",
    title: "You're Not Stuck Because You're Broken",
    slug: "not-stuck-not-broken",
    excerpt: "Feeling stuck isn't a character flaw. It's often a signal that you're ready for something new.",
    category: "Emotional Readiness",
    image_url: "https://images.pexels.com/photos/3385124/pexels-photo-3385124.png?w=600",
    created_at: new Date(Date.now() - 432000000).toISOString(),
    is_featured: false
  }
];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    filterPosts();
  }, [posts, selectedCategory, searchQuery]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      if (response.data.length > 0) {
        setPosts(response.data);
      } else {
        setPosts(samplePosts);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      setPosts(samplePosts);
    } finally {
      setIsLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = [...posts];
    
    if (selectedCategory !== "All") {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        post =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query)
      );
    }
    
    setFilteredPosts(filtered);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Please enter your email");
      return;
    }

    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("Thanks for subscribing! We'll be in touch.");
      setNewsletterEmail("");
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  const featuredPost = posts.find(post => post.is_featured);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center hero-dark" data-testid="blog-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-1/4 w-72 h-72 bg-brand-peach/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-brand-lavender/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-brand-peach font-medium tracking-widest uppercase text-sm mb-6"
          >
            Insights & Resources
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl font-normal text-text-light leading-tight mb-6"
            data-testid="blog-title"
          >
            The Become <span className="gradient-text">Blog</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-text-light/70 text-lg"
          >
            Stories, insights, and resources for your transformation journey.
          </motion.p>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16 bg-warm-bone" data-testid="blog-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-col lg:flex-row gap-6 mb-12">
            {/* Search */}
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full bg-white border-gray-200"
                data-testid="blog-search-input"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet"
                      : "bg-white text-text-dark/70 hover:bg-gray-100"
                  }`}
                  data-testid={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Featured Post */}
          {featuredPost && selectedCategory === "All" && !searchQuery && (
            <motion.div {...fadeUp} className="mb-12">
              <Link to={`/blog/${featuredPost.slug}`} className="block" data-testid="featured-post">
                <div className="relative overflow-hidden rounded-3xl bg-white shadow-lg group">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="aspect-video lg:aspect-auto">
                      <img
                        src={featuredPost.image_url || "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600"}
                        alt={featuredPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <span className="text-brand-peach text-sm font-medium mb-3">Featured</span>
                      <h2 className="font-heading text-2xl lg:text-3xl text-text-dark mb-4 group-hover:text-brand-primary transition-colors">
                        {featuredPost.title}
                      </h2>
                      <p className="text-text-dark/60 leading-relaxed mb-4">{featuredPost.excerpt}</p>
                      <div className="flex items-center gap-2 text-text-dark/40 text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(featuredPost.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {/* Posts Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts
                .filter(post => selectedCategory !== "All" || searchQuery || !post.is_featured)
                .map((post, index) => (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={`/blog/${post.slug}`} className="block h-full" data-testid={`blog-post-${post.slug}`}>
                      <article className="h-full bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 blog-card">
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={post.image_url || "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600"}
                            alt={post.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-6">
                          <span className="text-brand-peach text-xs font-medium uppercase tracking-wide">
                            {post.category}
                          </span>
                          <h3 className="font-heading text-xl text-text-dark mt-2 mb-3 line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-text-dark/60 text-sm leading-relaxed line-clamp-3 mb-4">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-text-dark/40 text-sm">
                            <Calendar className="w-4 h-4" />
                            {formatDate(post.created_at)}
                          </div>
                        </div>
                      </article>
                    </Link>
                  </motion.div>
                ))}
            </div>
          )}

          {filteredPosts.length === 0 && !isLoading && (
            <div className="text-center py-16">
              <p className="text-text-dark/60 text-lg">No articles found matching your criteria.</p>
            </div>
          )}
        </div>
      </section>

      {/* Transition to Contact */}
      <section className="py-20 bg-gradient-to-b from-warm-bone to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-brand-primary font-medium tracking-widest uppercase text-sm mb-4">
              Have Questions?
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-6">
              Get in Touch
            </h2>
            <p className="text-text-dark/60 text-lg mb-8 max-w-2xl mx-auto">
              We'd love to hear from you. Reach out with questions, feedback, or just to say hello.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet hover:opacity-90"
                data-testid="contact-transition-btn"
              >
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Newsletter & Signup Section */}
      <section className="py-20 hero-dark" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl text-text-light mb-4">
              Stay Updated
            </h2>
            <p className="text-text-light/60 mb-8">
              Get transformation insights delivered weekly and be the first to know when Become launches.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-8">
              <Input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-grow h-12 rounded-full bg-white/10 border-white/20 text-white placeholder:text-white/50"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={isSubscribing}
                className="h-12 px-6 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold hover:shadow-[0_0_20px_rgba(245,168,130,0.5)] transition-all"
                data-testid="newsletter-submit-btn"
              >
                {isSubscribing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
            <p className="text-center text-xs leading-relaxed text-white/40 max-w-md mx-auto mt-4" data-testid="newsletter-consent-text">
              By submitting, you agree to our <a href="/privacy" className="underline hover:text-white/60">Privacy Policy</a> and <a href="/terms" className="underline hover:text-white/60">Terms of Service</a>. We'll only use your information to contact you about Become. We never sell your data.
            </p>

            <div className="pt-6 border-t border-white/10">
              <p className="text-text-light/50 text-sm mb-4">Or join the waitlist for early access</p>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="h-10 px-6 rounded-full border-white/30 text-white hover:bg-white/10"
                  data-testid="signup-blog-btn"
                >
                  Join the Waitlist
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
